import React from "react";
import { Box, Container, useTheme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { FileText, Check, ArrowRight } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

const GenerateCVSection = () => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const theme = useTheme();
  const locale = i18nLocale;
  const features = [
    intl.formatMessage({ id: "generate_cv.point_1" }),
    intl.formatMessage({ id: "generate_cv.point_2" }),
    intl.formatMessage({ id: "generate_cv.point_3" }),
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "3rem", md: "5rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "hsl(178 93% 10%)",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: { xs: "2rem", md: "3rem" },
            alignItems: "center",
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "hsl(153 68% 25%)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: "1rem",
              }}
            >
              <FileText size={16} /> 
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.875rem",
                  color: "hsl(153 68% 25%)",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  letterSpacing: "0.02em",
                }}
              >
                <FormattedMessage id="generate_cv.new" />
              </Typography>
            </Box>

            <Box
              component="h2"
              sx={{
                fontSize: { xs: "1.875rem", md: "2.25rem" },
                color: theme.palette.common.white,
                letterSpacing: "-0.025em",
                mb: "1rem",
                lineHeight: 1.1,
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="generate_cv.title" />
            </Box>

            <Box
              component="p"
              sx={{
                color: alpha(theme.palette.common.white, 0.5),
                fontSize: "1rem",
                mb: "1.5rem",
                maxWidth: "32rem",
                lineHeight: 1.6,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="generate_cv.description" />
            </Box>

            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: "2rem" }}>
              {features.map((feature) => (
                <Box
                  key={feature}
                  component="li"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    color: alpha(theme.palette.common.white, 0.75),
                    fontSize: "0.875rem",
                    mb: "0.75rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  }}
                >
                  <Box
                    sx={{
                      width: "1.25rem",
                      height: "1.25rem",
                      borderRadius: "9999px",
                      bgcolor: "hsl(153 68% 25% / 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Check size={12} color="hsl(153 68% 40%)" />
                  </Box>
                  {feature}
                </Box>
              ))}
            </Box>

            <Box
              component="a"
              href={`/${locale}/candidates/sign_up`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                px: "1.5rem",
                py: "0.875rem",
                bgcolor: "hsl(153 68% 25%)",
                color: theme.palette.common.white,
                fontSize: "0.875rem",
                borderRadius: "0.75rem",
                textDecoration: "none",
                transition: "background-color 0.2s ease",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                "&:hover": {
                  bgcolor: "hsl(153 68% 25% / 0.9)",
                },
                "& .arrow-icon": {
                  transition: "transform 0.2s ease",
                },
                "&:hover .arrow-icon": {
                  transform: "translateX(2px)",
                },
              }}
            >
              <FormattedMessage id="generate_cv.call_to_action" />
              <ArrowRight size={14} className="arrow-icon" />
            </Box>
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            sx={{ position: "relative", display: "flex", justifyContent: "center" }}
          >
            <Box
              sx={{
                width: { xs: "18rem", md: "20rem" },
                bgcolor: theme.palette.background.paper,
                borderRadius: "0.75rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.3)",
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
              }}
            >
              <Box sx={{ bgcolor: "hsl(153 68% 25% / 0.1)", p: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
                <Box sx={{ width: "3.5rem", height: "3.5rem", borderRadius: "9999px", bgcolor: "hsl(153 68% 25% / 0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box component="span" sx={{ fontSize: "1.25rem", fontWeight: 900, color: "hsl(153 68% 25%)", fontFamily: "Segoe UI Black, system-ui, sans-serif" }}>CD</Box>
                </Box>
                <Box>
                  <Box component="p" sx={{ m: 0, fontWeight: 900, fontSize: "0.875rem", color: "hsl(178 93% 10%)", fontFamily: "Segoe UI Black, system-ui, sans-serif", letterSpacing: "0.01em" }}>Charlotte Dubois</Box>
                  <Box component="p" sx={{ m: 0, fontSize: "0.75rem", color: "hsl(178 10% 45%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}><FormattedMessage id="generate_cv.architect" /></Box>
                </Box>
              </Box>

              <Box sx={{ p: "1.25rem", display: "grid", gap: "1rem" }}>
                <Box>
                  <Box component="p" sx={{ m: 0, mb: "0.5rem", fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "hsl(153 68% 25%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                    <FormattedMessage id="generate_cv.presentation" />
                  </Box>
                  <Box sx={{ display: "grid", gap: "0.375rem" }}>
                    <Box sx={{ height: "0.5rem", bgcolor: "hsl(160 10% 90%)", borderRadius: "9999px", width: "100%" }} />
                    <Box sx={{ height: "0.5rem", bgcolor: "hsl(160 10% 90%)", borderRadius: "9999px", width: "80%" }} />
                    <Box sx={{ height: "0.5rem", bgcolor: "hsl(160 10% 90%)", borderRadius: "9999px", width: "60%" }} />
                  </Box>
                </Box>

                <Box>
                  <Box component="p" sx={{ m: 0, mb: "0.5rem", fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "hsl(153 68% 25%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                    <FormattedMessage id="generate_cv.experience" />
                  </Box>
                  {[intl.formatMessage({ id: "generate_cv.architect_example_1" }), intl.formatMessage({ id: "generate_cv.architect_example_2" })].map((role) => (
                    <Box key={role} sx={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", mb: "0.5rem" }}>
                      <Box sx={{ width: "0.375rem", height: "0.375rem", borderRadius: "9999px", bgcolor: "hsl(153 68% 25%)", mt: "0.35rem", flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box component="p" sx={{ m: 0, fontSize: "0.6875rem", fontWeight: 700, color: "hsl(178 93% 10%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>{role}</Box>
                        <Box sx={{ mt: "0.25rem", height: "0.375rem", bgcolor: "hsl(160 10% 90%)", borderRadius: "9999px", width: "75%" }} />
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Box>
                  <Box component="p" sx={{ m: 0, mb: "0.5rem", fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "hsl(153 68% 25%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                    <FormattedMessage id="generate_cv.hard_skills" />
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                    {[intl.formatMessage({ id: "generate_cv.autocad" }), intl.formatMessage({ id: "generate_cv.revit" }), intl.formatMessage({ id: "generate_cv.bim" }), intl.formatMessage({ id: "generate_cv.etudes_structurelles" }), intl.formatMessage({ id: "generate_cv.office_365" })].map((skill) => (
                      <Box key={skill} component="span" sx={{ fontSize: "0.5625rem", fontWeight: 500, bgcolor: "hsl(153 68% 25% / 0.1)", color: "hsl(153 68% 25%)", px: "0.5rem", py: "0.125rem", borderRadius: "9999px", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                        {skill}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ borderTop: "1px solid hsl(160 10% 90%)", px: "1.25rem", py: "0.625rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box component="span" sx={{ fontSize: "0.5rem", color: "hsl(178 10% 45%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}><FormattedMessage id="generate_cv.generated_by_squarehub" /></Box>
                <Box component="span" sx={{ fontSize: "0.5625rem", fontWeight: 700, color: "hsl(153 68% 25%)", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}><FormattedMessage id="generate_cv.squarehub" /></Box>
              </Box>
            </Box>

            <Box sx={{ position: "absolute", right: "-2.5rem", bottom: "-2.5rem", width: "10rem", height: "10rem", bgcolor: "hsl(153 68% 25% / 0.1)", borderRadius: "9999px", filter: "blur(48px)" }} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default GenerateCVSection;